export const MIN_PRESENTER_TIME = 60;

const slotMinimumSeconds = (presenter: Presenter): number => {
  if (presenter.slotType === 'Break') {
    const parsed = parseInt(presenter.description, 10);
    const breakMin = Number.isNaN(parsed) ? MIN_PRESENTER_TIME : parsed * 60;
    return Math.max(breakMin, MIN_PRESENTER_TIME);
  }
  if (presenter.slotType === 'Presenter' || presenter.slotType === '' || presenter.slotType === null) {
    return MIN_PRESENTER_TIME;
  }
  return 0;
};

/** Shortest total meeting length (from start) that still respects per-slot minimums and elapsed time. */
export const getMinimumMeetingLengthSeconds = (presenters: Presenter[], cursor: number): number => {
  let minLength = 0;
  for (let i = 0; i < presenters.length; i++) {
    if (i < cursor) {
      minLength += presenters[i].elapsedTime;
    } else if (i === cursor) {
      minLength += Math.max(slotMinimumSeconds(presenters[i]), presenters[i].elapsedTime);
    } else {
      minLength += slotMinimumSeconds(presenters[i]);
    }
  }
  return Math.max(minLength, 60);
};

export const getExpectedMeetingLength = (newPresenters: Presenter[], cursor: number): number => {
  let expectedMeetingLength = 0;
  for (let i = 0; i < newPresenters.length; i++) {
    if (i < cursor) {
      expectedMeetingLength += newPresenters[i].elapsedTime;
    } else if (i === cursor) {
      expectedMeetingLength += Math.max(newPresenters[i].elapsedTime, newPresenters[i].currentDuration);
    } else {
      expectedMeetingLength += newPresenters[i].currentDuration;
    }
  }
  return expectedMeetingLength
}

export const getCalculationTime = (time: number): number => {
  return time * 60;
}

export type AdjustPresentersTimeResult = {
  presenters: Presenter[];
  fitsMeetingLength: boolean;
};

export const adjustPresentersTime = (
  cursor: number,
  meetingLength: number,
  presenters: Presenter[]
): AdjustPresentersTimeResult => {

  //TODO: We can use bonus time instead of recalculating it here
  let expectedMeetingLength = getExpectedMeetingLength(presenters, cursor);

  if ((cursor === -1) || (presenters[cursor] && presenters[cursor].elapsedTime > 10)) cursor += 1;

  // If there is still bonus time, don't steal time
  // if (meetingLength === expectedMeetingLength) return presenters;

  let newPresenters = [...presenters];
  for (let i = cursor; i < presenters.length; i++) {
    expectedMeetingLength += (presenters[i].initialDuration - presenters[i].currentDuration)
    newPresenters[i].currentDuration = presenters[i].initialDuration
  }

  const overtimeSeconds = expectedMeetingLength - meetingLength;
  if (overtimeSeconds <= 0) {
    return { presenters: newPresenters, fitsMeetingLength: true };
  }

  // Bonus-only phase: no presenter slots left to shrink
  if (cursor >= presenters.length) {
    return { presenters: newPresenters, fitsMeetingLength: false };
  }

  return stealTimeEqualFixed(overtimeSeconds, newPresenters, cursor);

}

const stealTimeEqualFixed = (cumlativeOvertime: number, presenters: Presenter[], cursor: number): AdjustPresentersTimeResult => {

  let indexToSteal = cursor;
  let priorityLevelNew = -1;

  let newPresenters = [...presenters];


  const DEFUALT_AMOUNT_TO_STEAL = 30;
  let failedToStealFrom = 0;
  // let priorityLevel = 0;

  if (!priorityLevelNew) priorityLevelNew = -1;
  priorityLevelNew = Math.min(priorityLevelNew, 2);
  // priorityLevelNew = -1;

  if (indexToSteal < cursor || indexToSteal >= newPresenters.length) {
    indexToSteal = cursor;
  }

  // While we still have to steal time & there are people to steal from
  // We are checking the priority level isnt at the highest point so that we stop stealing after the Highest iteration 
  while (cumlativeOvertime > 0 && (failedToStealFrom <= newPresenters.length)) {
    let prevOvertime = cumlativeOvertime;
    let stopStealingFromMe = true;

    //If we have stolen from everyone in a certain priority level, we will increase the current priority level and continue to steal.
    if (failedToStealFrom >= newPresenters.length && priorityLevelNew < 2) {
      failedToStealFrom = 0;
      indexToSteal = cursor;
      priorityLevelNew = Math.min(priorityLevelNew + 1, 2);
    };

    [newPresenters, cumlativeOvertime, stopStealingFromMe, priorityLevelNew] = takeTimeFromPresenter(newPresenters, cumlativeOvertime, indexToSteal, MIN_PRESENTER_TIME, DEFUALT_AMOUNT_TO_STEAL, priorityLevelNew)


    if (cumlativeOvertime === prevOvertime) {
      failedToStealFrom += 1
    } else {
      failedToStealFrom = 0;
    }

    if (stopStealingFromMe) {
      indexToSteal += 1
    }
    if (indexToSteal < cursor || indexToSteal >= newPresenters.length) {
      indexToSteal = cursor;
    }
  }

  return {
    presenters: newPresenters,
    fitsMeetingLength: cumlativeOvertime <= 0,
  };
}


const takeTimeFromPresenter = (newPresenters: Presenter[], overtime: number, indexToSteal: number, MIN_PRESENTER_TIME: number, DEFUALT_AMOUNT_TO_STEAL: number, priorityToStealFrom: number): [Presenter[], number, boolean, number] => {
  let preferedTimeToSteal = DEFUALT_AMOUNT_TO_STEAL;
  let preferedMin = MIN_PRESENTER_TIME

  //BREAKS store min time in description
  if (newPresenters[indexToSteal].priority === -1) {
    preferedMin = parseInt(newPresenters[indexToSteal].description) * 60

  }
  let stopStealingFromMe = true

  // Case from if we stole from this person before - but only partially,
  // so we want to round to the nearest DEFAULT_AMOUNT_TO_STEAM increment
  // TODO: We need to consider elapsed time, we cant make current duration less than elapsed time.


  if (newPresenters[indexToSteal].priority <= priorityToStealFrom) {
    if (newPresenters[indexToSteal].currentDuration % DEFUALT_AMOUNT_TO_STEAL !== 0) {
      preferedTimeToSteal = newPresenters[indexToSteal].currentDuration % DEFUALT_AMOUNT_TO_STEAL;
    }

    const minAllowedDuration = Math.max(preferedMin, newPresenters[indexToSteal].elapsedTime ?? 0);
    const timeToSteal = Math.min(                                           // Steal the minimum of:
      overtime,                                                           // remaining overtime
      newPresenters[indexToSteal].currentDuration - minAllowedDuration,   // cannot shrink below elapsed or slot minimum
      preferedTimeToSteal                                                 // the prefered time to steal
    );
    newPresenters[indexToSteal].currentDuration -= timeToSteal;
    overtime -= timeToSteal;

    stopStealingFromMe = ((timeToSteal === preferedTimeToSteal) || (newPresenters[indexToSteal].currentDuration === minAllowedDuration));
  }
  return [newPresenters, overtime, stopStealingFromMe, priorityToStealFrom]

}
